import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';
import './i18n'; // Initialize i18n

import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import ShelterDashboard from './pages/ShelterDashboard';
import BreederDashboard from './pages/BreederDashboard';
import VetDashboard from './pages/VetDashboard';
import OwnerDashboard from './pages/OwnerDashboard';
import MicrochipSearch from './pages/MicrochipSearch';
import PaymentSuccess from './pages/PaymentSuccess';
import LostAndFoundPets from './pages/LostAndFoundPets';
import AdminDashboard from './pages/AdminDashboard';
import WebsiteBuilder from './pages/WebsiteBuilder';
import PartnersPage from './pages/PartnersPage';
import AcceptTransfer from './pages/AcceptTransfer';
import PublicWebsite from './pages/PublicWebsite';
import PublicForm from './pages/PublicForm';
import AIChatbot from './components/AIChatbot';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set axios defaults
axios.defaults.baseURL = API;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const getDashboard = () => {
    if (!user) return <Navigate to="/auth" />;
    
    switch (user.role) {
      case 'admin':
        return <AdminDashboard user={user} onLogout={handleLogout} />;
      case 'shelter':
        return <ShelterDashboard user={user} onLogout={handleLogout} />;
      case 'breeder':
        return <BreederDashboard user={user} onLogout={handleLogout} />;
      case 'vet':
        return <VetDashboard user={user} onLogout={handleLogout} />;
      case 'owner':
        return <OwnerDashboard user={user} onLogout={handleLogout} />;
      default:
        return <Navigate to="/" />;
    }
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage user={user} />} />
          <Route 
            path="/auth" 
            element={user ? <Navigate to="/dashboard" /> : <AuthPage onLogin={handleLogin} />} 
          />
          <Route path="/dashboard" element={getDashboard()} />
          <Route path="/search" element={<MicrochipSearch />} />
          <Route path="/lost-and-found" element={<LostAndFoundPets />} />
          <Route path="/payment-success" element={<PaymentSuccess user={user} />} />
          <Route path="/transfer-success" element={<PaymentSuccess user={user} type="transfer" />} />
          <Route path="/partner-success" element={<PaymentSuccess user={user} type="partner" />} />
          <Route path="/subscription-success" element={<PaymentSuccess user={user} type="subscription" />} />
          <Route 
            path="/website-builder" 
            element={
              user && ['shelter', 'breeder', 'vet'].includes(user.role) 
                ? <WebsiteBuilder user={user} onBack={() => window.history.back()} />
                : <Navigate to="/dashboard" />
            } 
          />
          <Route path="/partners" element={<PartnersPage user={user} />} />
          <Route path="/accept-transfer/:transferId" element={<AcceptTransfer />} />
          <Route path="/site/:subdomain" element={<PublicWebsite />} />
          <Route path="/forms/:templateId" element={<PublicForm />} />
        </Routes>
        <AIChatbot user={user} />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
